import { useEffect, useMemo, useRef, useState } from 'react';
import { LIST_LIMIT_DEFAULT, type ListInput } from '@servwell/shared';
import type { ServerTable, Sort } from '@/components/ui/data-table';
import { useDebounced } from './useDebounced';

/**
 * The page-side half of a server-driven table: what to ask the server for, and what to
 * hand back to <DataTable server={…}>.
 *
 * One hook rather than the same six useStates on six pages — not to save typing, but
 * because the rules that keep it honest are easy to forget one page at a time. Typing in
 * the search box has to reset to page one (otherwise you land on page 4 of 2 results and
 * see nothing), and the request has to wait until typing stops (otherwise every keystroke
 * is a query and the answers race each other back). Per-column filters (102) follow both
 * rules for the same reasons: they are typed into, and they change the result set.
 */
export function useServerTable(
  pageSize = LIST_LIMIT_DEFAULT,
  /**
   * Anything ELSE the page narrows the result set by, that this hook cannot see — a
   * "Show retired" toggle, a kind tab, a date range. It joins the reset key, because the
   * rule is about the RESULT SET changing, not about which control changed it: without
   * it, un-ticking a filter on page 4 asks the server for rows 150-200 of a set that now
   * has 12, and the footer reads "0 of 12" until the table heals itself a render later.
   * Serialise it yourself (a string or a number) so the identity is stable per render.
   */
  scope?: string | number | boolean,
) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search).trim();
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<Sort>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  // Debounce the SERIALIZED filters — the object's identity changes per keystroke, but
  // the wire only needs the settled value, and page reset must key on real change.
  const debouncedFilters = useDebounced(JSON.stringify(filters));

  // A new search or filter set means a new result set; whatever page you were on no
  // longer exists. The key is a JSON ARRAY, not a concat — a concat is not injective
  // (the columnSetKey law, forty lines from here in the table this feeds).
  //
  // ⚠️ The reset must reach the QUERY in the same render the settled value changes.
  // An effect alone runs after render, so the first post-settle request carried the
  // STALE offset — a wasted query answering a page that no longer exists (found by the
  // 102 adversary). `effectivePage` answers synchronously; the effect then makes the
  // state agree.
  const key = JSON.stringify([debounced, debouncedFilters, scope ?? null]);
  const lastQuery = useRef(key);
  const effectivePage = lastQuery.current === key ? page : 0;
  useEffect(() => {
    if (lastQuery.current !== key) {
      lastQuery.current = key;
      setPage(0);
    }
  }, [key]);

  // Parsed once per settled value, not on every render.
  const activeFilters = useMemo(() => {
    const settled = JSON.parse(debouncedFilters) as Record<string, string>;
    return Object.fromEntries(Object.entries(settled).filter(([, v]) => v.trim() !== ''));
  }, [debouncedFilters]);

  const query: ListInput = {
    search: debounced || undefined,
    limit: pageSize,
    offset: effectivePage * pageSize,
    sort: sort?.key,
    dir: sort?.dir,
    filters: Object.keys(activeFilters).length ? activeFilters : undefined,
  };

  /** Spread into <DataTable server={…}> once the query has answered. */
  function table(total: number, loading?: boolean): ServerTable {
    return {
      total,
      page: effectivePage,
      onPage: setPage,
      pageSize,
      sort,
      onSort: setSort,
      filters,
      onFilters: setFilters,
      loading,
    };
  }

  return { search, setSearch, query, table };
}
